/**
 * Mows that came too soon after the last one.
 *
 * Owner, Sep 30 2026: "Make sure some of the mows weren't the free mows to
 * reschedule them if they weren't atleast 12 days for biweekly or 6 days for
 * weekly then free" — automatically. Month-end makes the ones not on a bill
 * yet (or on a draft not sent yet) free on its own (services/tooSoon). This
 * card is for the rest: one on a bill already sent is fixed and sent again in
 * one press; a paid one needs a refund or a credit; one booked on its own (it
 * may be an extra they asked for) is his call. Shows only when there is
 * something to see. Oct 26 2026 ("same with the make-the-mow-free feature"):
 * folded to one line — "2 mows under the gap this month · 1 made free
 * automatically, 1 your call" — that opens on a tap (FoldCard). Oct 27 2026:
 * "Keep charging" on every row — a route that ran late and then moved days put
 * six regular visits 10 days apart, his call to charge them — stamps the mow
 * so the rule leaves it alone (jobs.too_soon_waived_at) and it leaves the list.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import FoldCard from './FoldCard';

interface TooSoonMow {
  job_id: number; customer_id: number; customer_name: string;
  address?: string | null; property_label?: string | null;
  day: string; prev_day: string; gap: number; cadence: 'weekly' | 'biweekly'; min_gap: number;
  auto: boolean; hold: string | null;
  price: number;
  invoice: { id: number; invoice_number: string; status: string; line_amount: number } | null;
}

const money = (n: any) => `$${(Number(n) || 0).toFixed(2)}`;
const day = (iso: string) => new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
/** Pure: the folded line — how many mows came under the gap, and what became of them. */
export const foldWords = (mows: Pick<TooSoonMow, 'auto' | 'invoice'>[]): string => {
  if (!mows.length) return 'No mows under the gap this month.';
  const free = mows.filter(m => !m.invoice && m.auto).length;
  const paid = mows.filter(m => m.invoice?.status === 'paid').length;
  const yours = mows.length - free - paid;
  const parts = [];
  if (free) parts.push(`${free} made free automatically`);
  if (yours) parts.push(`${yours} your call`);
  if (paid) parts.push(`${paid} already paid`);
  return `${plural(mows.length, 'mow')} under the gap this month · ${parts.join(', ')}`;
};
const howSoon = (m: TooSoonMow) => (Number(m.gap) === 0
  ? `Same day as the last mow (${m.cadence})`
  : `${m.gap} day${m.gap === 1 ? '' : 's'} after the last mow (${m.cadence})`);

export default function TooSoonCard() {
  const qc = useQueryClient();
  const { data } = useQuery('billing-too-soon',
    () => api.get('/billing/too-soon').then(r => r.data as { from: string; to: string; mows: TooSoonMow[] }),
    { retry: false });
  const mows: TooSoonMow[] = data?.mows || [];

  const makeFree = useMutation((jobId: number) => api.post(`/billing/too-soon/${jobId}/make-free`).then(r => r.data), {
    onSuccess: (d: any) => {
      // Made free, but the corrected bill did not go out: say so in amber, not green.
      const notSent = !!d?.invoice_id && Array.isArray(d?.sent) && !d.sent.length && !['draft', 'paid'].includes(String(d?.status));
      (notSent ? toast.warn : toast.success)(d?.message || 'Made free', { autoClose: 9000 });
      qc.invalidateQueries('billing-too-soon'); qc.invalidateQueries('invoices'); qc.invalidateQueries('billing-dashboard');
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || 'Could not make that mow free');
      qc.invalidateQueries('billing-too-soon');
    },
  });

  const keep = useMutation((jobId: number) => api.post(`/billing/too-soon/${jobId}/keep-charging`).then(r => r.data), {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'Charging it as normal', { autoClose: 6000 });
      qc.invalidateQueries('billing-too-soon');
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || 'Could not keep charging that mow');
      qc.invalidateQueries('billing-too-soon');
    },
  });
  const anyBusy = makeFree.isLoading || keep.isLoading;

  if (!mows.length) return null;

  return (
    <FoldCard storageKey="billing_fold_too_soon" tone="amber" title="Mows too soon after the last one" summary={foldWords(mows)}>
      <p className="text-xs text-gray-600">Weekly yards need 6 days between mows, every-other-week yards 12 (5 after a first mow). One that came sooner is free — the mow only; extras and add-ons still bill and the crew is still paid.</p>
      <ul className="divide-y divide-gray-100">
        {mows.map(m => {
          const inv = m.invoice;
          const paid = inv?.status === 'paid';
          const busy = makeFree.isLoading && makeFree.variables === m.job_id;
          const keeping = keep.isLoading && keep.variables === m.job_id;
          const keepButton = (
            <button type="button" disabled={anyBusy}
              onClick={() => {
                if (window.confirm(`Keep charging ${m.customer_name}'s ${day(m.day)} mow (${money(m.price)})? It stays on the bill as normal and leaves this list.`)) keep.mutate(m.job_id);
              }}
              className="w-full sm:w-auto text-xs px-3 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white disabled:opacity-50">
              {keeping ? 'Working…' : 'Keep charging'}
            </button>
          );
          return (
            <li key={m.job_id} className="py-2 flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 text-sm">
                <p className="font-semibold text-gray-900 truncate">{m.customer_name} · {day(m.day)}</p>
                <p className="text-xs text-gray-600">{howSoon(m)} · {money(m.price)}</p>
                <p className="text-xs text-gray-500 truncate">
                  {m.property_label || m.address || ''}{(m.property_label || m.address) ? ' · ' : ''}
                  {inv ? `On bill ${inv.invoice_number} (${inv.status})` : 'Not on a bill yet'}
                </p>
                {!m.auto && m.hold && <p className="text-xs text-amber-800">{m.hold} — your call.</p>}
              </div>
              <div className="flex-shrink-0 flex flex-col gap-1.5 sm:items-end">
                {!inv && m.auto && (
                  <p className="text-xs text-emerald-700 font-medium">Will be free on the month-end bill automatically</p>
                )}
                {!inv && !m.auto && (
                  <button type="button" disabled={anyBusy}
                    onClick={() => {
                      if (window.confirm(`Make ${m.customer_name}'s ${day(m.day)} mow free? ${money(m.price)} comes off it on the next bill.`)) makeFree.mutate(m.job_id);
                    }}
                    className="w-full sm:w-auto text-xs px-3 py-2 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">
                    {busy ? 'Working…' : 'Make it free'}
                  </button>
                )}
                {inv && paid && (
                  <p className="text-xs text-amber-800 font-medium">Already paid — refund or credit</p>
                )}
                {inv && !paid && (
                  <button type="button" disabled={anyBusy}
                    onClick={() => {
                      if (window.confirm(`Make ${m.customer_name}'s ${day(m.day)} mow free? ${money(m.price)} comes off ${inv.invoice_number}${inv.status === 'draft' ? '.' : ' and the corrected bill goes to them again (or it is settled if nothing is owed).'}`)) makeFree.mutate(m.job_id);
                    }}
                    className="w-full sm:w-auto text-xs px-3 py-2 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">
                    {busy ? 'Working…' : inv.status === 'draft' ? 'Make it free' : 'Make it free & resend'}
                  </button>
                )}
                {keepButton}
              </div>
            </li>
          );
        })}
      </ul>
    </FoldCard>
  );
}
